import { apiService } from './ApiService';

// Auth types
interface LoginRequest {
  username: string;
  password: string;
}

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: {
    id: string;
    username: string;
    email: string;
    full_name: string;
    role: string;
    status: string;
  };
}

interface User {
  id: string;
  username: string;
  email: string;
  full_name: string;
  role: string;
  status: string;
  created_at: string;
  updated_at: string;
  last_login?: string;
}

class AuthService {
  private readonly TOKEN_KEY = 'access_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly USER_KEY = 'user_info';

  // Login
  async login(credentials: LoginRequest): Promise<LoginResponse | null> {
    try {
      const response = await apiService.post<LoginResponse>('/api/v1/auth/login', credentials);
      
      if (response.data) {
        this.setTokens(response.data.access_token, response.data.refresh_token);
        this.setUser(response.data.user);
        return response.data;
      }
      
      throw new Error(response.error || 'Login failed');
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  // SSO Login
  async loginWithSSO(): Promise<void> {
    const ssoUrl = 'http://localhost:8001/api/v1/auth/sso/login';
    window.location.href = ssoUrl;
  }

  // Logout
  async logout(): Promise<void> {
    try {
      await apiService.post('/api/v1/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.clearAuth();
    }
  }

  // Get current user
  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await apiService.get<User>('/api/v1/auth/me');
      
      if (response.data) {
        this.setUser(response.data);
        return response.data;
      }
      
      return null;
    } catch (error) {
      console.error('Get current user error:', error);
      this.clearAuth();
      return null;
    }
  }

  // Refresh token
  async refreshToken(): Promise<string | null> {
    try {
      const refreshToken = this.getRefreshToken();
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await apiService.post<{ access_token: string }>('/api/v1/auth/refresh', {
        refresh_token: refreshToken
      });

      if (response.data) {
        this.setToken(response.data.access_token);
        return response.data.access_token;
      }

      throw new Error(response.error || 'Token refresh failed');
    } catch (error) {
      console.error('Token refresh error:', error);
      this.clearAuth();
      return null;
    }
  }

  // Change password
  async changePassword(currentPassword: string, newPassword: string): Promise<boolean> {
    try {
      const response = await apiService.post('/api/v1/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });

      return response.status === 200;
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  }

  // Token management
  setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(this.TOKEN_KEY, accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
  }

  setToken(accessToken: string): void {
    localStorage.setItem(this.TOKEN_KEY, accessToken);
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  // User management
  setUser(user: any): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  getUser(): User | null {
    try {
      const userStr = localStorage.getItem(this.USER_KEY);
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  }

  // Auth state
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  isAdmin(): boolean {
    const user = this.getUser();
    return user?.role === 'admin';
  }

  hasRole(role: string): boolean {
    const user = this.getUser();
    return user?.role === role;
  }

  // Clear auth data
  clearAuth(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
  }

  // Token validation
  async verifyToken(): Promise<boolean> {
    try {
      const response = await apiService.get('/api/v1/auth/verify-token');
      return response.status === 200;
    } catch (error) {
      console.error('Token verification error:', error);
      this.clearAuth();
      return false;
    }
  }
}

export const authService = new AuthService();
export default AuthService;
